import React, { useCallback, useEffect, useState } from "react";

import { ExpandableToggle } from "../../components/expandableToggle";

import { Account } from "src/types/accounts";

import _ from "lodash";
import styles from "./styles.module.scss";

export interface ShareSettings {
  download: boolean;
  reshare: boolean;
  gated: boolean;
  overrideGatingFormTemplateUrl: null;
  downloadEditable: boolean;
  validateEmailDomain: boolean;
  validateEmailOtp: boolean;
  engage: boolean;
}

interface Option {
  key: string;
  label: string;
  selected?: boolean;
};

interface ShareSettingsProps {
  data: Partial<Account>;
  handleAccountSettingsUpdate: (value: any) => void;
}

const ShareSettings: React.FC<ShareSettingsProps> = ({ data, handleAccountSettingsUpdate }) => {
  const [shareSettings, setShareSettings] = useState({
    download: true,
    reshare: true,
    gated: true,
    overrideGatingFormTemplateUrl: null,
    downloadEditable: true,
    validateEmailDomain: false,
    validateEmailOtp: false,
    engage: true,
  });

  useEffect(() => {
    if (data?.shareSettings) {
      setShareSettings(prev => ({ ...prev, ...data.shareSettings }));
    }
  }, [data?.shareSettings]);

  const updateShareSettings = useCallback((updatedData: Partial<ShareSettings>) => {
    setShareSettings(prev => {
      const updatedShareSettings = { ...prev, ...updatedData };
      handleAccountSettingsUpdate(updatedShareSettings);
      return updatedShareSettings;
    });
  }, [handleAccountSettingsUpdate]);


  const downloadOptions: Option[] = [
    {
      key: "downloadEditable",
      label: "Editable",
      selected: shareSettings.downloadEditable,
    },
    {
      key: "downloadReadOnly",
      label: "Read only",
      selected: !shareSettings.downloadEditable,
    },
  ];

  const gatingOptions: Option[] = [
    {
      key: "validateEmailDomain",
      label: "Validate work email",
      selected: shareSettings.validateEmailDomain,
    },
    {
      key: "validateEmailOtp",
      label: "Validate OTP",
      selected: shareSettings.validateEmailOtp,
    },
  ];

  const onDownloadOptionsChange = (option: Option) => {
    updateShareSettings({
      downloadEditable: option.key === "downloadReadOnly" ? option.selected : !option.selected,
    })
  };

  const onGatingOptionsChange = (option: Option) => {
    updateShareSettings({
      [option.key]: !option.selected,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.toggleRow}>
        <ExpandableToggle
          id="download"
          title="Download"
          description="Indicate whether to enable download for each share"
          value={shareSettings.download}
          onChange={(value) => updateShareSettings({ download: value })}
          options={downloadOptions}
          onOptionChange={onDownloadOptionsChange}
          helpText={
            <div className={styles.helpContent}>
              <span className={styles.helpText}>*</span>
              <span>This applies to PPTs, Docs, Google Slides and Google Docs.</span>
            </div>
          }
          collapsible={false}
        />

        <ExpandableToggle
          id="reshare"
          title="Reshare"
          description="Indicate whether to enable reshare for each share"
          value={shareSettings.reshare}
          onChange={(value) => updateShareSettings({ reshare: value })}
          collapsible={false}
        />
      </div>

      <div className={styles.toggleRow}>
        <ExpandableToggle
          id="gating"
          title="Gating"
          description="Indicate whether to enable gating for each share"
          value={shareSettings.gated}
          onChange={(value) => updateShareSettings({ gated: value })}
          options={gatingOptions}
          onOptionChange={onGatingOptionsChange}
          collapsible={false}
        />
         <ExpandableToggle
          id="engage"
          title="Engage"
          description="Indicate whether to enable engage for each share"
          value={shareSettings.engage}
          onChange={(value) => updateShareSettings({ engage: value })}
          collapsible={false}
        />
      </div>
    </div>
  );
};

export default ShareSettings;